/**
 * @file     semanticColors.ts
 * @readme   ./README.md
 * @purpose  Factory that maps a raw ColorPalette to a typed SemanticColorTokens object (backgrounds, surfaces, borders, text, interactive states, status colors) conditioned on light/dark mode, plus the hub role colors (link, chip, wash, glass, scrim). Returns CSS custom property references by default; use createRawSemanticColors / createRawSemanticRoleColors for hex values (needed by alpha() calls).
 */
import type { ColorPalette, SemanticColorTokens, ThemeMode } from '@/core/types';

/**
 * Maps each SemanticColorTokens key to a short CSS custom property name.
 * These vars are defined on `html[data-theme="light|dark"]` by the boot CSS
 * so the correct values are active before React hydrates, eliminating
 * flash-of-wrong-theme for all Emotion-generated component styles.
 */
export const SEMANTIC_CSS_VARS: Record<keyof SemanticColorTokens, string> = {
  backgroundPrimary: '--r-bg',
  backgroundSecondary: '--r-bg2',
  surfaceCard: '--r-surface',
  surfaceElevated: '--r-surface-el',
  surfaceRecessed: '--r-surface-recessed',
  surfaceInput: '--r-surface-input',
  borderSubtle: '--r-border',
  borderStrong: '--r-border-strong',
  divider: '--r-divider',
  textPrimary: '--r-text',
  textSecondary: '--r-text2',
  textMuted: '--r-muted',
  textOnPrimary: '--r-text-on-primary',
  interactivePrimary: '--r-primary',
  interactiveSecondary: '--r-secondary',
  interactiveHover: '--r-hover',
  interactivePressed: '--r-pressed',
  interactiveFocusRing: '--r-focus',
  statusSuccess: '--r-success',
  statusWarning: '--r-warning',
  statusError: '--r-error',
  statusInfo: '--r-info',
  favoriteActive: '--r-fav',
  videoPlayBg: '--r-vid-bg',
  videoPlayHover: '--r-vid-hover',
  videoPlayIcon: '--r-vid-icon',
  mapTileTextShadow: '--r-map-shadow',
};

/**
 * Returns semantic color tokens as CSS `var(--r-*)` references.
 * Components should use these values for all color properties so that
 * the boot-time CSS custom properties control the actual color,
 * enabling instant theme switching without a React re-render.
 */
export const createSemanticColorTokens = (
  _mode: ThemeMode,
  _palette: ColorPalette
): SemanticColorTokens => {
  const result = {} as SemanticColorTokens;
  for (const key of Object.keys(SEMANTIC_CSS_VARS) as (keyof SemanticColorTokens)[]) {
    result[key] = `var(${SEMANTIC_CSS_VARS[key]})`;
  }
  return result;
};

/**
 * Returns semantic color tokens as raw hex/rgba values.
 * Use this when you need the actual color value, e.g. for MUI `alpha()`
 * calls, canvas drawing, or anywhere a CSS var reference won't work.
 */
export const createRawSemanticColors = (
  mode: ThemeMode,
  palette: ColorPalette
): SemanticColorTokens => {
  const isDark = mode === 'dark';

  return {
    backgroundPrimary: palette.semantic.background,
    backgroundSecondary: palette.interaction.hoverSurface,
    surfaceCard: palette.semantic.surface,
    surfaceElevated: palette.interaction.hoverElevated,
    surfaceRecessed: palette.semantic.surfaceRecessed,
    surfaceInput: palette.semantic.surfaceInput,
    borderSubtle: palette.semantic.border,
    borderStrong: palette.neutral[isDark ? 500 : 300],
    divider: palette.semantic.divider,
    textPrimary: palette.semantic.textPrimary,
    textSecondary: palette.semantic.textSecondary,
    textMuted: palette.semantic.textMuted,
    textOnPrimary: isDark ? palette.neutral[900] : palette.neutral[0],
    interactivePrimary: palette.brand.primary,
    interactiveSecondary: palette.brand.secondary,
    interactiveHover: palette.interaction.hoverElevated,
    interactivePressed: palette.interaction.pressedSurface,
    interactiveFocusRing: palette.interaction.focusRing,
    statusSuccess: palette.state.success,
    statusWarning: palette.state.warning,
    statusError: palette.state.error,
    statusInfo: palette.state.info,
    favoriteActive: palette.state.error,
    // YouTube brand colors, must match YouTube's official play button styling
    videoPlayBg: 'rgba(0, 0, 0, 0.8)',
    videoPlayHover: '#cc0000',
    videoPlayIcon: palette.neutral[0],
    // Map overlay text shadow for neighborhood labels
    mapTileTextShadow: 'rgba(0, 0, 0, 0.5)',
  };
};

/**
 * Role colors the hub redesign needs that the core SemanticColorTokens set has
 * no name for. Kept as a separate map so no existing `--r-*` variable changes
 * meaning: the prototype's `--r-border` and `--r-hover` are NOT these names.
 * Every value derives from a palette ramp step; translucent roles apply an
 * alpha to a ramp step.
 */
export const SEMANTIC_ROLE_CSS_VARS = {
  link: '--r-link',
  linkHover: '--r-link-hover',
  primaryHover: '--r-primary-hover',
  chip: '--r-chip',
  chipText: '--r-chip-text',
  heroWash: '--r-hero-wash',
  glass: '--r-glass',
  scrim: '--r-scrim',
  successSoft: '--r-success-soft',
  skeleton: '--r-skeleton',
  amberText: '--r-amber-text',
  surfaceSunken: '--r-surface-sunken',
} as const;

const withAlpha = (hex: string, opacity: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${opacity})`;
};

/** Role colors as `var(--r-*)` references; mode-agnostic, safe in server components. */
export const semanticRoleColors = Object.fromEntries(
  Object.entries(SEMANTIC_ROLE_CSS_VARS).map(([key, cssVar]) => [key, `var(${cssVar})`])
) as Record<keyof typeof SEMANTIC_ROLE_CSS_VARS, string>;

/** Role colors as raw hex/rgba values for alpha() math and the boot CSS. */
export const createRawSemanticRoleColors = (
  mode: ThemeMode,
  palette: ColorPalette
): Record<keyof typeof SEMANTIC_ROLE_CSS_VARS, string> => {
  const isDark = mode === 'dark';

  return {
    link: isDark ? palette.accent[700] : palette.brand.primary,
    linkHover: isDark ? palette.accent[800] : palette.primary[600],
    primaryHover: isDark ? palette.primary[700] : palette.primary[600],
    chip: isDark ? palette.primary[100] : palette.primary[50],
    chipText: isDark ? palette.primary[700] : palette.primary[600],
    heroWash: palette.primary[0],
    // Sticky bars only; pair with glassBlur.
    glass: withAlpha(palette.semantic.surface, isDark ? 0.82 : 0.8),
    scrim: withAlpha(palette.neutral[900], isDark ? 0.72 : 0.48),
    successSoft: withAlpha(palette.state.success, isDark ? 0.16 : 0.12),
    skeleton: isDark ? palette.neutral[700] : palette.neutral[100],
    // Step 800 in both themes: light secondary[700] measures 4.45:1 on white, under AA.
    amberText: palette.secondary[800],
    surfaceSunken: isDark ? palette.semantic.surfaceRecessed : palette.interaction.hoverSurface,
  };
};
